import { useAudioContext } from "../use-audio-context";
import { useCallback, useMemo } from "react";
import { IAudioControls } from "./use-audio.types";

/**
 * Perform actions on the audioRef from useAudioContext.
 * @returns controls that manipulate the audioRef i.e pause, play, seek, playbackRate, volume, skipBackward, skipForward, toggleMute.
 */
const useAudio = () => {
    const { audioRef } = useAudioContext();

    const pause = useCallback(() => {
        if (audioRef?.current) {
            audioRef?.current.pause();
        }
    }, [audioRef]);

    const play = useCallback(async () => {
        if (audioRef?.current) {
            await audioRef?.current.play();
        }
    }, [audioRef]);

    const seek = useCallback(
        (time: number) => {
            if (audioRef?.current) {
                audioRef.current.currentTime = time;
            }
        },
        [audioRef]
    );

    const playbackRate = useCallback(
        (rate: number) => {
            if (audioRef?.current) {
                audioRef.current.playbackRate = rate;
            }
        },
        [audioRef]
    );

    const volume = useCallback(
        (volume: number) => {
            if (audioRef?.current) {
                audioRef.current.volume = volume;
            }
        },
        [audioRef]
    );

    const skipBackward = useCallback(
        (seconds: number) => {
            if (audioRef?.current) {
                audioRef.current.currentTime -= seconds;
            }
        },
        [audioRef]
    );

    const skipForward = useCallback(
        (seconds: number) => {
            if (audioRef?.current) {
                audioRef.current.currentTime += seconds;
            }
        },
        [audioRef]
    );

    const toggleMute = useCallback(() => {
        if (audioRef?.current) {
            audioRef.current.muted = !audioRef?.current.muted;

            if (audioRef.current.muted) {
                audioRef.current.volume = 0;
            } else {
                audioRef.current.volume = 1;
            }
        }
    }, [audioRef]);

    const controls: IAudioControls = useMemo(
        () => ({
            pause,
            play,
            seek,
            playbackRate,
            volume,
            skipBackward,
            skipForward,
            toggleMute,
        }),
        [pause, play, seek, playbackRate, volume, skipBackward, skipForward, toggleMute]
    );

    return controls;
};

export default useAudio;
